import React, { useEffect, useState } from "react";
import { Button, Divider, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { connect } from "react-redux";
import { FormValidation } from "../../../core/utility";
import { LocalService } from "../../../core/services";
import { UserService } from "../../../core/services";
import * as gtUtil from "../../../core/utility";

const localService = new LocalService();

const userService = new UserService();
interface Props {
    UserData: any;
}

const PasswordSecurity: React.FC<Props> = (props: Props) => {
    const formValidation = new FormValidation();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isEditingPassword, setIsEditingPassword] = useState(true);
    // Credentials stay masked by default; each field gets its own eye toggle.
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [timerState, setTimerState] = useState({
        isDisabledPassword: false,
        passwordTimeLeft: 0
    });

    const [errors, setErrors] = useState<{
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }>({});

    const handleCurrentPasswordChange = (val: string) => {
        setCurrentPassword(val);
        if (errors.currentPassword) {
            setErrors((prev) => ({ ...prev, currentPassword: "" }));
        }
    };

    const handleNewPasswordChange = (val: string) => {
        setNewPassword(val);
        if (errors.newPassword) {
            setErrors((prev) => ({ ...prev, newPassword: "" }));
        }
    };

    const handleConfirmPasswordChange = (val: string) => {
        setConfirmPassword(val);
        if (errors.confirmPassword) {
            setErrors((prev) => ({ ...prev, confirmPassword: "" }));
        }
    };

    const handleSave = async () => {
        const isEmpty = (val: string) => !val.trim();
        const newErrors: { [key: string]: string } = {};

        if (isEditingPassword) {
            // Empty field checks
            if (isEmpty(currentPassword)) {
                newErrors.currentPassword = "Current password is required";
            }
            if (isEmpty(newPassword)) {
                newErrors.newPassword = "New password is required";
            }
            if (isEmpty(confirmPassword)) {
                newErrors.confirmPassword = "Please confirm your new password";
            }

            // Proceed if all fields are filled
            if (!isEmpty(currentPassword) && !isEmpty(newPassword) && !isEmpty(confirmPassword)) {
                if (newPassword !== confirmPassword) {
                    newErrors.confirmPassword = "Passwords do not match";
                }

                const passwordValidation = formValidation.GetPasswordValidate(newPassword);
                if (!passwordValidation.status) {
                    newErrors.newPassword = passwordValidation.message;
                }
            }

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }

            setErrors({}); // Clear errors if all validations pass

            try {
                const response = await userService.updatePassword({
                    data: {
                        old_password: currentPassword,
                        new_password: newPassword,
                        confirm_password: confirmPassword,
                        user_id: props.UserData.user_id,
                    },
                });

                if (response.status === gtUtil.Constants.SUCCESS) {
                    localService.toastify("Password Updated", "success", 1000);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                } else {
                    localService.toastify(response.info, "error", 1000);
                    if (response.info === 'Current password is incorrect') {
                        setErrors({ currentPassword: response.info });
                    }
                }
            } catch (error) {
                localService.toastify("Password update failed", "error", 1000);
            }
        }
    };

    const forgetPasswordPin = (login: string, forgot: string) => {
        if (!login.trim()) {
            localService.toastify("Valid Email or Mobile Number is required", "error", 1000);
            return;
        }

        userService.forgetPasswordPin({ login, forgot }).then((res) => {
            if (res.status === gtUtil.Constants.SUCCESS) {
                localService.toastify(res.info, "success", 1000);

                const countdownTime = 180; // 3 minutes

                setTimerState((prevState) => ({
                    ...prevState,
                    isDisabledPassword: forgot === "forgot password" ? true : prevState.isDisabledPassword,
                    passwordTimeLeft: forgot === "forgot password" ? countdownTime : prevState.passwordTimeLeft
                }));

                setTimeout(() => {
                    setTimerState((prevState) => ({
                        ...prevState,
                        isDisabledPassword: forgot === "forgot password" ? false : prevState.isDisabledPassword
                    }));
                }, countdownTime * 1000);
            } else {
                setTimerState({
                    isDisabledPassword: false,
                    passwordTimeLeft: 0
                });
                localService.toastify(res.info, "error", 1000);
            }
        });
    };

    useEffect(() => {
        if (timerState.passwordTimeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimerState((prevState) => ({
                ...prevState,
                passwordTimeLeft: prevState.passwordTimeLeft > 0 ? prevState.passwordTimeLeft - 1 : 0,
            }));
        }, 1000);

        return () => clearInterval(timer);
    }, [timerState.passwordTimeLeft]);

    return (
        <Grid container direction={{ xs: "column", sm: "row" }}>
            <Grid item xs={12}>
                <span className="fs-16 d-none d-md-block" style={{ marginBottom: 10 }}>Password</span>
                <Grid container spacing={2}>
                    <Grid item xs={12} className="d-flex flex-column align-items-end">
                        <TextField
                            label="Current Password"
                            type={showCurrentPassword ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => handleCurrentPasswordChange(e.target.value)}
                            fullWidth
                            size="small"
                            className="input-common"
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={`${showCurrentPassword ? "Hide" : "Show"} current password`}
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            edge="end"
                                            size="small"
                                        >
                                            {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div className="d-flex pb-2">
                            <span
                                style={{
                                    fontSize: '12px',
                                    color: timerState.isDisabledPassword ? '#aaa' : '#000',
                                    cursor: timerState.isDisabledPassword ? 'not-allowed' : 'pointer',
                                    pointerEvents: timerState.isDisabledPassword ? 'none' : 'auto'
                                }}
                                onClick={() => {
                                    if (!timerState.isDisabledPassword) {
                                        forgetPasswordPin(props.UserData.login_email, "forgot password");
                                    }
                                }}
                            >
                                Forgot Password?
                            </span>

                            {timerState.isDisabledPassword && timerState.passwordTimeLeft > 0 && (
                                <span className="error" style={{ marginLeft: '8px', fontSize: '0.8rem' }}>
                                    {Math.floor(timerState.passwordTimeLeft / 60)}:
                                    {(timerState.passwordTimeLeft % 60).toString().padStart(2, "0")}
                                </span>
                            )}
                        </div>
                    </Grid>
                    <Grid item xs={12} className="pt-0">
                        <TextField
                            label="New Password"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => handleNewPasswordChange(e.target.value)}
                            fullWidth
                            size="small"
                            className="input-common"
                            error={!!errors.newPassword}
                            helperText={errors.newPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={`${showNewPassword ? "Hide" : "Show"} new password`}
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            edge="end"
                                            size="small"
                                        >
                                            {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Confirm Password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                            fullWidth
                            size="small"
                            className="input-common"
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={`${showConfirmPassword ? "Hide" : "Show"} confirm password`}
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            edge="end"
                                            size="small"
                                        >
                                            {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            className="btn btn-outline-primary upload_button"
                            onClick={handleSave}
                        >
                            Update Password
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const mapStateToProps = (state: any) => {
    return {
        UserData: state.UserReducer.UserData,
    };
};
const connectToRedux = connect(mapStateToProps, null)(PasswordSecurity);
export { connectToRedux as PasswordSecurity };